<script setup>
import { computed, ref, defineEmits } from 'vue';
import { createToaster } from '@meforma/vue-toaster';
import BitrixAccountLinkGuideButton from '@/components/CreateConnectionComponents/BitrixUpload/components/BitrixAccountLinkGuideButton.vue';
import { Api } from '@/service/api';

const emit = defineEmits(['back', 'oauth-started']);

const toaster = createToaster();
const showDiscardWarning = ref(false);
const isSubmitting = ref(false);

const BITRIX_PORTAL_DOMAIN_REGEX = /^[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?\.bitrix24\.ru$/i;
const BITRIX_APP_INSTALL_PATH = '/marketplace/detail/tergum.tergum_app/';
const BITRIX_APP_INSTALL_QUERY = 'ver=1&install=Y&hash=85712996b76ef8b291ad2c9458aef051&check_hash=75aadfcd000002303799942763167316&install_hash=3ff39c62b3c3382f9d0ed533c741b7cd';

const form = ref({
    portalDomain: '',
});

const isDirty = computed(() => Boolean(form.value.portalDomain.trim()));

function parsePortalDomain(input) {
    const value = input.trim();

    if (!value) {
        return null;
    }

    if (/^https?:\/\//i.test(value)) {
        try {
            return new URL(value).hostname.toLowerCase();
        } catch {
            return null;
        }
    }

    return value.split('/')[0].toLowerCase();
}

function isValidPortalDomain(domain) {
    return Boolean(domain && BITRIX_PORTAL_DOMAIN_REGEX.test(domain));
}

function buildAppInstallUrl(portalDomain) {
    return `https://${portalDomain}${BITRIX_APP_INSTALL_PATH}?${BITRIX_APP_INSTALL_QUERY}`;
}

function validateAppInstallUrl(urlString) {
    try {
        const url = new URL(urlString);

        if (url.protocol !== 'https:') {
            return 'Ссылка должна начинаться с https://';
        }

        if (!isValidPortalDomain(url.hostname)) {
            return 'Укажите корректный домен Битрикс24 вида company.bitrix24.ru';
        }

        if (url.pathname !== BITRIX_APP_INSTALL_PATH) {
            return 'Некорректный путь к приложению в маркетплейсе Битрикс24';
        }

        return null;
    } catch {
        return 'Не удалось сформировать ссылку на приложение';
    }
}

function resetForm() {
    form.value = {
        portalDomain: '',
    };
}

function requestBack() {
    if (isDirty.value) {
        showDiscardWarning.value = true;
        return;
    }

    emit('back');
}

function cancelDiscard() {
    showDiscardWarning.value = false;
}

function confirmDiscard() {
    resetForm();
    showDiscardWarning.value = false;
    emit('back');
}

async function submitAttach() {
    const portalDomain = parsePortalDomain(form.value.portalDomain);

    if (!portalDomain) {
        toaster.error('Укажите домен портала Битрикс24');
        return;
    }

    if (!isValidPortalDomain(portalDomain)) {
        toaster.error('Некорректный домен. Укажите адрес вида company.bitrix24.ru');
        return;
    }

    isSubmitting.value = true;

    try {
        const redirectUri = await Api.getBitrixOAuthUrl(portalDomain);
        console.log("REDIRECT_URI ", redirectUri)

        emit('oauth-started', portalDomain);
        window.open(
            redirectUri,
            '_blank',
            'top=100,left=100,width=600,height=700',
        );
        resetForm();
    } catch (error) {
        console.error('Bitrix OAuth start failed', error);
        toaster.error('Не удалось начать привязку аккаунта Bitrix');
    } finally {
        isSubmitting.value = false;
    }
}

function openAppLink() {
    const portalDomain = parsePortalDomain(form.value.portalDomain);

    if (!portalDomain) {
        toaster.error('Укажите домен портала Битрикс24');
        return;
    }

    if (!isValidPortalDomain(portalDomain)) {
        toaster.error('Некорректный домен. Укажите адрес вида company.bitrix24.ru');
        return;
    }

    const appUrl = buildAppInstallUrl(portalDomain);
    const validationError = validateAppInstallUrl(appUrl);

    if (validationError) {
        toaster.error(validationError);
        return;
    }

    window.open(appUrl, '_blank', 'noopener,noreferrer');
}
</script>

<template>
    <div class="relative">
        <div class="flex items-start justify-between gap-3 px-6 pt-6 pb-4 border-b border-gray-100">
            <div class="flex-1 min-w-0 pr-2">
                <h2 id="bitrix-account-attach-title" class="text-lg font-semibold text-gray-900">
                    Привязка аккаунта Bitrix
                </h2>
                <p class="text-sm text-gray-500 mt-1">
                    Укажите данные авторизации вашего портала Битрикс24
                </p>
            </div>

            <div class="flex items-start gap-2 flex-shrink-0">
                <BitrixAccountLinkGuideButton />

                <button
                    type="button"
                    class="flex items-center justify-center w-8 h-8 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors"
                    aria-label="Закрыть"
                    @click="requestBack"
                >
                    <svg class="w-5 h-5" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                        <path fill-rule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clip-rule="evenodd" />
                    </svg>
                </button>
            </div>
        </div>

        <form class="px-6 py-5 space-y-4" @submit.prevent="submitAttach">
            <div>
                <label for="bitrix-portal-domain" class="block text-sm font-medium text-gray-700 mb-1.5">
                    Домен портала
                </label>
                <input
                    id="bitrix-portal-domain"
                    v-model="form.portalDomain"
                    type="text"
                    placeholder="company.bitrix24.ru"
                    class="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                >
            </div>

            <div class="flex items-center gap-3">
                <button
                    type="button"
                    class="flex-1 py-2.5 px-4 rounded-lg border border-blue-500 text-blue-600 hover:bg-blue-50 text-sm font-semibold transition-colors duration-150"
                    @click="openAppLink"
                >
                    Установить приложение
                </button>
                <button
                    type="submit"
                    class="flex-1 py-2.5 px-4 rounded-lg bg-blue-500 hover:bg-blue-600 text-white text-sm font-semibold transition-colors duration-150 disabled:opacity-60 disabled:cursor-not-allowed"
                    :disabled="isSubmitting"
                >
                    {{ isSubmitting ? 'Привязка...' : 'Привязать аккаунт' }}
                </button>
            </div>
        </form>

        <Transition name="modal-fade">
            <div
                v-if="showDiscardWarning"
                class="absolute inset-0 z-10 flex items-center justify-center p-4 bg-gray-900/40 rounded-md"
            >
                <div class="w-full max-w-sm bg-white rounded-lg shadow-lg border border-gray-100 p-5">
                    <h3 class="text-base font-semibold text-gray-900">
                        Прервать привязку?
                    </h3>
                    <p class="text-sm text-gray-500 mt-2 leading-relaxed">
                        Введённые данные авторизации будут потеряны. Вы вернётесь к списку аккаунтов.
                    </p>

                    <div class="flex items-center justify-end gap-2 mt-5">
                        <button
                            type="button"
                            class="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
                            @click="cancelDiscard"
                        >
                            Отмена
                        </button>
                        <button
                            type="button"
                            class="px-4 py-2 text-sm font-medium text-white bg-red-500 rounded-lg hover:bg-red-600 transition-colors"
                            @click="confirmDiscard"
                        >
                            Вернуться к списку
                        </button>
                    </div>
                </div>
            </div>
        </Transition>
    </div>
</template>

<style scoped>
.modal-fade-enter-active,
.modal-fade-leave-active {
    transition: opacity 0.2s ease;
}

.modal-fade-enter-from,
.modal-fade-leave-to {
    opacity: 0;
}
</style>
